import { useMemo } from "react";
import { AlertTriangle, Save, Lock, LogOut } from "lucide-react";
import { cn } from "@/lib/utils";
import type { GracePhase } from "@/contexts/MaintenanceContext";
import { CountdownTimer } from "./CountdownTimer";

type AutoSaveStatus = "idle" | "saving" | "saved" | "saved-locally" | "error";

interface MaintenanceBannerProps {
  phase: GracePhase;
  autoSaveStatus: AutoSaveStatus;
  reason?: string;
  gracePeriodEndsAt: Date;
  onPhaseChange?: (phase: GracePhase) => void;
  onComplete?: () => void;
}

interface PhaseConfig {
  heightClass: string;
  bgClass: string;
  fgClass: string;
  Icon: React.FC<{ className?: string }>;
  iconSize: string;
  message: string;
  overlay: number;
}

const AUTO_SAVE_LABELS: Record<AutoSaveStatus, string> = {
  idle: "",
  saving: "Salvando...",
  saved: "✓ Trabalho salvo",
  "saved-locally": "✓ Salvo localmente",
  error: "⚠ Erro ao salvar",
};

const AUTO_SAVE_COLORS: Record<AutoSaveStatus, string> = {
  idle: "",
  saving: "text-amber-600 dark:text-amber-400",
  saved: "text-green-600 dark:text-green-400",
  "saved-locally": "text-green-600 dark:text-green-400",
  error: "text-red-600 dark:text-red-400",
};

function getPhaseConfig(phase: NonNullable<GracePhase>): PhaseConfig {
  switch (phase) {
    case "warn":
      return {
        heightClass: "h-12",
        bgClass: "bg-maintenance-warn",
        fgClass: "text-maintenance-warn-foreground",
        Icon: AlertTriangle,
        iconSize: "h-12 w-12",
        message: "Manutenção em {countdown}. Seu trabalho será salvo automaticamente.",
        overlay: 0,
      };
    case "save":
      return {
        heightClass: "h-16",
        bgClass: "bg-maintenance-save",
        fgClass: "text-maintenance-save-foreground",
        Icon: Save,
        iconSize: "h-16 w-16",
        message: "Finalize e salve seu trabalho.",
        overlay: 0,
      };
    case "readonly":
      return {
        heightClass: "h-16",
        bgClass: "bg-maintenance-readonly",
        fgClass: "text-maintenance-readonly-foreground",
        Icon: Lock,
        iconSize: "h-16 w-16",
        message: "Dados seguros. Sistema entrando em manutenção.",
        overlay: 0.3,
      };
    case "logout":
      return {
        heightClass: "h-full",
        bgClass: "bg-maintenance-logout",
        fgClass: "text-maintenance-logout-foreground",
        Icon: LogOut,
        iconSize: "h-16 w-16",
        message: "Redirecionando...",
        overlay: 0.7,
      };
  }
}

export const MaintenanceBanner: React.FC<MaintenanceBannerProps> = ({
  phase,
  autoSaveStatus,
  gracePeriodEndsAt,
  onPhaseChange,
  onComplete,
}) => {
  const config = useMemo(() => (phase ? getPhaseConfig(phase) : null), [phase]);

  if (!phase || !config) return null;

  const { heightClass, bgClass, fgClass, Icon, iconSize, message, overlay } = config;

  const messageBeforeCountdown = message.split("{countdown}")[0];
  const messageAfterCountdown = message.split("{countdown}")[1] ?? "";
  const hasCountdown = message.includes("{countdown}");

  return (
    <>
      {/* Overlay for readonly/logout phases */}
      {overlay > 0 && (
        <div
          className="fixed inset-0 bg-black z-[9998] pointer-events-none"
          style={{ opacity: overlay }}
          aria-hidden="true"
        />
      )}

      <div
        role="alert"
        aria-live="polite"
        className={cn(
          "fixed top-0 left-0 right-0 z-[9999] flex items-center justify-center gap-3 px-4",
          heightClass,
          bgClass,
          fgClass,
          phase === "warn" && "animate-pulse-warn",
          "animate-fade-phase"
        )}
      >
        <Icon className={cn(iconSize, "shrink-0")} />
        <span className="text-sm font-medium">
          {hasCountdown ? (
            <>
              {messageBeforeCountdown}
              <CountdownTimer
                targetTime={gracePeriodEndsAt}
                size="sm"
                format="mm:ss"
                onPhaseChange={onPhaseChange}
                onComplete={onComplete}
              />
              {messageAfterCountdown}
            </>
          ) : (
            message
          )}
        </span>
        {autoSaveStatus !== "idle" && (
          <span
            aria-live="polite"
            className={cn(
              "text-sm font-medium ml-2 motion-reduce:transition-none",
              AUTO_SAVE_COLORS[autoSaveStatus]
            )}
          >
            {AUTO_SAVE_LABELS[autoSaveStatus]}
          </span>
        )}
      </div>
    </>
  );
};
